import { useEffect, useState } from 'react';
import { listarAlertas, listarAlertasCliente, avaliarAlertas,
         tratarAlerta, catalogoAlertas, configurarAlerta } from '../api';
import { PageHeader, Loading, Empty, Alert, KPI, Badge } from '../components/common';
import { fmtMoeda } from '../lib/format';

// Cor vem do tema: no escuro o texto e claro e o fundo e um veu da mesma cor.
// Hexadecimal fixo aqui deixava texto escuro sobre superficie escura.
const NATUREZAS = {
  decisao: {
    rotulo: 'Decisão', cor: 'var(--al-critica-fg)', fundo: 'var(--al-critica-bg)',
    chamada: 'Exige julgamento técnico — só o contador resolve',
    icone: '⚖',
  },
  tarefa: {
    rotulo: 'Tarefa', cor: 'var(--al-media-fg)', fundo: 'var(--al-media-bg)',
    chamada: 'O dado já está aqui, falta executar',
    icone: '▶',
  },
  verificacao: {
    rotulo: 'Verificação', cor: 'var(--al-alta-fg)', fundo: 'var(--al-alta-bg)',
    chamada: 'Indício que pode ser normal — confirmar antes de concluir',
    icone: '?',
  },
  documento: {
    rotulo: 'Documento', cor: 'var(--al-baixa-fg)', fundo: 'var(--al-baixa-bg)',
    chamada: 'Falta papel do cliente — nada se resolve aqui dentro',
    icone: '✉',
  },
};

const SEVERIDADES = {
  critica: { rotulo: 'Crítica', cor: 'var(--al-critica-fg)', fundo: 'var(--al-critica-bg)', peso: 1 },
  alta:    { rotulo: 'Alta',    cor: 'var(--al-alta-fg)',    fundo: 'var(--al-alta-bg)',    peso: 2 },
  media:   { rotulo: 'Média',   cor: 'var(--al-media-fg)',   fundo: 'var(--al-media-bg)',   peso: 3 },
  baixa:   { rotulo: 'Baixa',   cor: 'var(--al-baixa-fg)',   fundo: 'var(--al-baixa-bg)',   peso: 4 },
};

const Selo = ({ s }) => {
  const d = SEVERIDADES[s] || SEVERIDADES.baixa;
  return (
    <span style={{ background: d.fundo, color: d.cor, border: '1px solid currentColor', borderColor: d.cor, opacity: 1,
                   padding: '2px 8px', borderRadius: 10, fontSize: 11,
                   fontWeight: 700, whiteSpace: 'nowrap' }}>
      {d.rotulo}
    </span>
  );
};

export default function Alertas({ clienteId }) {
  const [aba, setAba] = useState('carteira');
  const [dados, setDados] = useState(null);
  const [doCliente, setDoCliente] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [filtroSev, setFiltroSev] = useState('');
  const [filtroRegra, setFiltroRegra] = useState('');
  const [filtroNat, setFiltroNat] = useState('');
  const [status, setStatus] = useState('aberto');
  const [aberto, setAberto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avaliando, setAvaliando] = useState(false);
  const [msg, setMsg] = useState(null);

  const carregar = () => {
    setLoading(true);
    const p = [listarAlertas({ status, severidade: filtroSev || undefined,
                               regra: filtroRegra || undefined,
                               natureza: filtroNat || undefined })];
    p.push(clienteId ? listarAlertasCliente(clienteId, status) : Promise.resolve({ itens: [] }));
    p.push(catalogoAlertas());
    Promise.allSettled(p).then(([a, b, c]) => {
      if (a.status === 'fulfilled') setDados(a.value);
      if (b.status === 'fulfilled') setDoCliente(b.value.itens || []);
      if (c.status === 'fulfilled') setCatalogo(c.value.itens || []);
      setLoading(false);
    });
  };
  useEffect(carregar, [clienteId, status, filtroSev, filtroRegra, filtroNat]);

  const avaliar = async (soCliente) => {
    setAvaliando(true);
    setMsg(null);
    try {
      const r = await avaliarAlertas(soCliente ? clienteId : null);
      setMsg({ tipo: 'success',
               txt: `${r.clientes} cliente(s) avaliados em ${r.duracao_ms} ms: `
                    + `${r.alertas} alerta(s) ativos, ${r.fechados} fechado(s).` });
      carregar();
    } catch (e) {
      setMsg({ tipo: 'danger', txt: e.message });
    } finally {
      setAvaliando(false);
    }
  };

  const tratar = async (id, acao) => {
    let obs = null;
    if (acao === 'ignorar') {
      obs = prompt('Por que este alerta pode ser dispensado?');
      if (!obs || obs.trim().length < 5) return;
    } else if (acao === 'resolver') {
      obs = prompt('O que foi feito? (opcional)') || null;
    }
    try {
      await tratarAlerta(id, acao, obs);
      setMsg({ tipo: 'success', txt: `Alerta #${id}: ${acao}.` });
      setAberto(null);
      carregar();
    } catch (e) {
      setMsg({ tipo: 'danger', txt: e.message });
    }
  };

  const ligar = async (regra, ativo) => {
    try {
      await configurarAlerta(regra, { ativo });
      carregar();
    } catch (e) {
      setMsg({ tipo: 'danger', txt: e.message });
    }
  };

  if (loading && !dados) return <Loading texto="Lendo os alertas…" />;

  const resumo = dados?.resumo || { porNatureza: [], porSeveridade: [], porRegra: [], porCliente: [] };
  const conta = (s) => Number(resumo.porSeveridade.find(x => x.severidade === s)?.qtd || 0);
  const soma = resumo.porSeveridade.reduce((t, x) => t + Number(x.valor || 0), 0);
  const itens = aba === 'cliente' ? doCliente : (dados?.itens || []);

  return (
    <>
      <PageHeader
        title="Central de alertas"
        sub="O que o sistema encontrou sozinho — limites, enquadramento, retenção e escrituração"
        actions={
          <>
            {clienteId && (
              <button onClick={() => avaliar(true)} disabled={avaliando}>
                {avaliando ? 'Avaliando…' : 'Reavaliar este cliente'}
              </button>
            )}
            <button className="primary" onClick={() => avaliar(false)} disabled={avaliando}>
              {avaliando ? 'Avaliando…' : 'Reavaliar a carteira'}
            </button>
          </>
        }
      />

      {msg && <Alert tipo={msg.tipo}>{msg.txt}</Alert>}

      {/* Natureza antes de severidade: severidade diz quanto dói,
          natureza diz o que fazer — e com quem. */}
      <div className="kpi-grid">
        {(dados?.ordemNatureza || ['decisao', 'tarefa', 'verificacao', 'documento']).map(nat => {
          const d = NATUREZAS[nat];
          const linhas = resumo.porNatureza.filter(x => x.natureza === nat);
          const qtd = linhas.reduce((s, x) => s + Number(x.qtd || 0), 0);
          const val = linhas.reduce((s, x) => s + Number(x.valor || 0), 0);
          const graves = linhas.reduce((s, x) => s + Number(x.graves || 0), 0);
          const ativo = filtroNat === nat;
          return (
            <div key={nat}
                 onClick={() => setFiltroNat(ativo ? '' : nat)}
                 title={d.chamada}
                 style={{ cursor: 'pointer', border: `1px solid ${ativo ? d.cor : '#d5dce8'}`,
                          borderRadius: 8, padding: '12px 14px',
                          background: ativo ? d.fundo : 'var(--surface)' }}>
              <div style={{ fontSize: 11, color: d.cor, fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: .4 }}>
                {d.icone} {d.rotulo}
              </div>
              <div style={{ fontSize: 26, fontWeight: 700, margin: '4px 0 2px' }}>
                {qtd}
                {graves > 0 && (
                  <span style={{ fontSize: 12, color: d.cor, fontWeight: 600, marginLeft: 8 }}>
                    {graves} grave{graves > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              <div className="small muted">{d.chamada}</div>
              {val > 0 && (
                <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, color: d.cor }}>
                  {fmtMoeda(val)}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="toolbar">
        <button className={aba === 'carteira' ? 'primary' : ''} onClick={() => setAba('carteira')}>
          Carteira ({dados?.count || 0})
        </button>
        {clienteId && (
          <button className={aba === 'cliente' ? 'primary' : ''} onClick={() => setAba('cliente')}>
            Este cliente ({doCliente.length})
          </button>
        )}
        <button className={aba === 'gatilhos' ? 'primary' : ''} onClick={() => setAba('gatilhos')}>
          Gatilhos ({catalogo.filter(g => g.ativo).length})
        </button>
        <span style={{ flex: 1 }} />
        <select value={status} onChange={e => setStatus(e.target.value)}>
          <option value="aberto">Abertos</option>
          <option value="resolvido">Resolvidos</option>
          <option value="ignorado">Ignorados</option>
          <option value="todos">Todos</option>
        </select>
        <select value={filtroSev} onChange={e => setFiltroSev(e.target.value)}>
          <option value="">Toda severidade</option>
          {Object.entries(SEVERIDADES).map(([k, v]) => (
            <option key={k} value={k}>{v.rotulo}</option>
          ))}
        </select>
        <select value={filtroRegra} onChange={e => setFiltroRegra(e.target.value)}>
          <option value="">Todo gatilho</option>
          {catalogo.map(g => <option key={g.regra} value={g.regra}>{g.nome}</option>)}
        </select>
      </div>

      {aba === 'gatilhos' ? (
        <table className="tabela">
          <thead>
            <tr>
              <th style={{ width: 70 }}>Ativo</th>
              <th>Gatilho</th>
              <th style={{ width: 104 }}>Natureza</th>
              <th>Ação</th>
              <th style={{ textAlign: 'right' }}>Abertos</th>
              <th style={{ textAlign: 'right' }}>Já detectados</th>
            </tr>
          </thead>
          <tbody>
            {catalogo.map(g => (
              <tr key={g.regra}>
                <td>
                  <input type="checkbox" checked={g.ativo}
                         onChange={e => ligar(g.regra, e.target.checked)} />
                </td>
                <td><strong>{g.nome}</strong><div className="small muted">{g.regra}</div></td>
                <td>
                  <span style={{ fontSize: 11, fontWeight: 700,
                                 color: NATUREZAS[g.natureza]?.cor }}>
                    {NATUREZAS[g.natureza]?.icone} {NATUREZAS[g.natureza]?.rotulo}
                  </span>
                </td>
                <td className="small">{g.acao}</td>
                <td style={{ textAlign: 'right' }}>
                  {g.abertos ? <Badge tipo="warning">{g.abertos}</Badge> : '—'}
                </td>
                <td style={{ textAlign: 'right' }} className="small muted">{g.total || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : itens.length === 0 ? (
        <Empty icon="✓" title="Nada a apontar"
               sub="Nenhum alerta aberto com os filtros atuais." />
      ) : (
        <>
          {aba === 'carteira' && resumo.porRegra.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '0 0 12px' }}>
              {resumo.porRegra.map(r => (
                <button key={r.regra}
                        onClick={() => setFiltroRegra(filtroRegra === r.regra ? '' : r.regra)}
                        style={{ fontSize: 12,
                                 fontWeight: filtroRegra === r.regra ? 700 : 400 }}>
                  {catalogo.find(g => g.regra === r.regra)?.nome || r.regra}
                  {' · '}{r.qtd}
                  {Number(r.valor) > 0 && <> · {fmtMoeda(r.valor)}</>}
                </button>
              ))}
            </div>
          )}

          <table className="tabela">
            <thead>
              <tr>
                <th style={{ width: 96 }}>Natureza</th>
                <th style={{ width: 78 }}>Severidade</th>
                {aba === 'carteira' && <th>Cliente</th>}
                <th>O que foi encontrado</th>
                <th style={{ width: 80 }}>Comp.</th>
                <th style={{ width: 130, textAlign: 'right' }}>Valor</th>
                <th style={{ width: 150 }}></th>
              </tr>
            </thead>
            <tbody>
              {itens.map(a => (
                <>
                  <tr key={a.id} onClick={() => setAberto(aberto === a.id ? null : a.id)}
                      style={{ cursor: 'pointer' }}>
                    <td>
                      {a.natureza && (
                        <span style={{ fontSize: 11, fontWeight: 700,
                                       color: NATUREZAS[a.natureza]?.cor,
                                       background: NATUREZAS[a.natureza]?.fundo,
                                       border: '1px solid currentColor',
                                       padding: '2px 7px', borderRadius: 10,
                                       whiteSpace: 'nowrap' }}>
                          {NATUREZAS[a.natureza]?.icone} {NATUREZAS[a.natureza]?.rotulo}
                        </span>
                      )}
                    </td>
                    <td><Selo s={a.severidade} /></td>
                    {aba === 'carteira' && (
                      <td className="small">{a.razao_social}</td>
                    )}
                    <td><strong>{a.titulo}</strong></td>
                    <td className="small muted">{a.competencia || a.referencia || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      {a.valor ? fmtMoeda(a.valor) : '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {a.status === 'aberto' ? (
                        <>
                          <button className="small" onClick={e => { e.stopPropagation(); tratar(a.id, 'resolver'); }}>
                            Tratado
                          </button>
                          <button className="small" onClick={e => { e.stopPropagation(); tratar(a.id, 'ignorar'); }}>
                            Dispensar
                          </button>
                        </>
                      ) : (
                        <button className="small" onClick={e => { e.stopPropagation(); tratar(a.id, 'reabrir'); }}>
                          Reabrir
                        </button>
                      )}
                    </td>
                  </tr>
                  {aberto === a.id && (
                    <tr key={`${a.id}-d`}>
                      <td colSpan={aba === 'carteira' ? 7 : 6}
                          style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                        {a.acao && (
                          <p style={{ margin: '0 0 8px', fontWeight: 600,
                                      color: NATUREZAS[a.natureza]?.cor }}>
                            {NATUREZAS[a.natureza]?.icone} {a.acao}
                            {a.responsavel && (
                              <span className="small muted" style={{ fontWeight: 400 }}>
                                {' '}· responsável: {a.responsavel === 'cliente'
                                  ? 'o cliente envia' : 'o escritório executa'}
                              </span>
                            )}
                          </p>
                        )}
                        <p style={{ margin: '0 0 8px', lineHeight: 1.55 }}>{a.detalhe}</p>
                        {a.fundamento && (
                          <p className="small muted" style={{ margin: '0 0 6px' }}>
                            <strong>Base legal:</strong> {a.fundamento}
                          </p>
                        )}
                        <p className="small muted" style={{ margin: 0 }}>
                          Gatilho <code>{a.regra}</code> · detectado em{' '}
                          {new Date(a.detectado_em).toLocaleDateString('pt-BR')}
                          {a.status !== 'aberto' && <> · {a.status} por {a.resolvido_por}</>}
                          {a.observacao && <> · {a.observacao}</>}
                        </p>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </>
      )}

      {aba === 'carteira' && resumo.porCliente.length > 0 && status === 'aberto' && (
        <>
          <h3 style={{ marginTop: 24 }}>Onde a exposição está concentrada</h3>
          <table className="tabela">
            <thead>
              <tr>
                <th>Cliente</th>
                <th style={{ width: 90, textAlign: 'right' }}>Alertas</th>
                <th style={{ width: 150, textAlign: 'right' }}>Exposição</th>
              </tr>
            </thead>
            <tbody>
              {resumo.porCliente.slice(0, 15).map(c => (
                <tr key={c.cliente_id}>
                  <td>{c.razao_social}</td>
                  <td style={{ textAlign: 'right' }}>{c.qtd}</td>
                  <td style={{ textAlign: 'right' }}>{fmtMoeda(c.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </>
  );
}
